import React, { useState } from 'react';
import { db, auth } from './firebase';
import { collection, addDoc, doc, updateDoc, serverTimestamp } from 'firebase/firestore';

const S = {
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: '#0A0E1Aee', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: '#111827', border: '1px solid #1E3A5F', borderRadius: 12, padding: 24, width: 560, maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto' },
  title: { fontSize: 18, fontWeight: 900, color: '#F8FAFC', marginBottom: 4 },
  sub: { fontSize: 12, color: '#9CA3AF', marginBottom: 20 },
  label: { fontSize: 11, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700, marginBottom: 6, display: 'block', marginTop: 14 },
  input: { background: '#1E3A5F22', border: '1px solid #1E3A5F', color: '#F8FAFC', padding: '10px 14px', borderRadius: 8, fontSize: 13, width: '100%', boxSizing: 'border-box' },
  select: { background: '#111827', border: '1px solid #1E3A5F', color: '#F8FAFC', padding: '10px 14px', borderRadius: 8, fontSize: 13, width: '100%', boxSizing: 'border-box' },
  btn: (color) => ({ background: color || '#1E3A5F', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 700 }),
  pointRow: { display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: '#1E3A5F22', borderRadius: 8, marginBottom: 6 },
  dayBtn: (selected) => ({ background: selected ? '#1E3A5F' : 'transparent', border: `1px solid ${selected ? '#1E3A5F' : '#1E3A5F44'}`, color: selected ? '#fff' : '#9CA3AF', padding: '6px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: selected ? 700 : 400 }),
};

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export default function PatrolModal({ route, tenantId, onClose, onSaved }) {
  const isEdit = !!route;
  const [form, setForm] = useState({
    name: route?.name || '',
    site: route?.site || '',
    schedule: route?.schedule || 'one-off',
    schedule_days: route?.schedule_days || [],
    schedule_time: route?.schedule_time || '06:00',
    active: route?.active !== false,
  });
  const [points, setPoints] = useState(route?.points || []);
  const [newPoint, setNewPoint] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const toggleDay = (day) => {
    const days = form.schedule_days.includes(day)
      ? form.schedule_days.filter(d => d !== day)
      : [...form.schedule_days, day];
    set('schedule_days', days);
  };

  const addPoint = () => {
    if (!newPoint.trim()) return;
    const pointId = `PP${String(points.length + 1).padStart(3, '0')}`;
    setPoints([...points, {
      point_id: pointId,
      label: newPoint.trim(),
      order: points.length + 1,
      nfc_tag_id: null,
      time_limit_minutes: 5,
    }]);
    setNewPoint('');
  };

  const removePoint = (index) => {
    setPoints(points.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.site.trim()) {
      setError('Route name and site are required');
      return;
    }
    if (points.length === 0) {
      setError('Add at least one patrol point');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const user = auth.currentUser;
      const data = {
        ...form,
        points: points.map((p, i) => ({ ...p, order: i + 1 })),
        company_id: tenantId,
        updated_at: serverTimestamp(),
      };
      if (isEdit && route._id) {
        await updateDoc(doc(db, 'patrol_routes', route._id), data);
      } else {
        await addDoc(collection(db, 'patrol_routes'), {
          ...data,
          created_by: user?.uid,
          created_by_email: user?.email,
          created_at: serverTimestamp(),
        });
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={S.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={S.modal}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div>
            <div style={S.title}>{isEdit ? 'Edit patrol route' : 'Create patrol route'}</div>
            <div style={S.sub}>Define the route, points and schedule.</div>
          </div>
          <button onClick={onClose} style={{ ...S.btn('#D32F2F22'), color: '#D32F2F', padding: '6px 12px' }}>✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <label style={S.label}>Route name *</label>
              <input style={S.input} placeholder="e.g. Night perimeter" value={form.name} onChange={e => set('name', e.target.value)} required />
            </div>
            <div style={{ flex: 1 }}>
              <label style={S.label}>Site *</label>
              <input style={S.input} placeholder="e.g. Paddington Square" value={form.site} onChange={e => set('site', e.target.value)} required />
            </div>
          </div>

          <label style={S.label}>Schedule</label>
          <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
            {['one-off', 'daily', 'weekly'].map(s => (
              <button key={s} type="button"
                style={{ ...S.btn(form.schedule === s ? '#1E3A5F' : 'transparent'), border: '1px solid #1E3A5F44', color: form.schedule === s ? '#fff' : '#9CA3AF', padding: '8px 16px' }}
                onClick={() => set('schedule', s)}>{s.charAt(0).toUpperCase() + s.slice(1)}</button>
            ))}
          </div>

          {form.schedule === 'weekly' && (
            <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
              {DAYS.map(d => (
                <button key={d} type="button" style={S.dayBtn(form.schedule_days.includes(d))} onClick={() => toggleDay(d)}>{d}</button>
              ))}
            </div>
          )}

          {form.schedule !== 'one-off' && (
            <div>
              <label style={S.label}>Start time</label>
              <input style={{ ...S.input, width: 140 }} type="time" value={form.schedule_time} onChange={e => set('schedule_time', e.target.value)} />
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14 }}>
            <label style={{ ...S.label, marginTop: 0 }}>Active</label>
            <input type="checkbox" checked={form.active} onChange={e => set('active', e.target.checked)} style={{ width: 18, height: 18 }} />
          </div>

          <label style={S.label}>Patrol points ({points.length})</label>
          {points.map((p, i) => (
            <div key={i} style={S.pointRow}>
              <div style={{ width: 28, height: 28, borderRadius: 6, background: '#1E3A5F', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#fff', flexShrink: 0 }}>{i + 1}</div>
              <div style={{ flex: 1, fontSize: 13, color: '#F8FAFC' }}>{p.label}</div>
              <div style={{ fontSize: 10, color: '#9CA3AF', fontFamily: 'monospace' }}>{p.point_id}</div>
              <button type="button" onClick={() => removePoint(i)} style={{ background: 'none', border: 'none', color: '#D32F2F', cursor: 'pointer', fontSize: 16 }}>✕</button>
            </div>
          ))}
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <input style={{ ...S.input, flex: 1 }} placeholder="Point name (e.g. Main entrance)" value={newPoint}
              onChange={e => setNewPoint(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addPoint())} />
            <button type="button" onClick={addPoint} style={S.btn()}>+ Add</button>
          </div>

          {error && <div style={{ color: '#D32F2F', fontSize: 12, marginTop: 8 }}>{error}</div>}

          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            <button type="button" onClick={onClose} style={{ ...S.btn('#1E3A5F44'), flex: 1 }}>Cancel</button>
            <button type="submit" disabled={loading} style={{ ...S.btn('#2E7D32'), flex: 2 }}>
              {loading ? 'Saving…' : isEdit ? 'Update route' : 'Create route'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}